import { Box, HStack, Icon, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { CrashType, Score, ScoreEntry } from "@keita/lanepatrol-sdk";
import { BicycleIcon, CarIcon, WalkIcon } from "../../shared/views/icons";
import { IconType } from "react-icons/lib";
import {
  formatRiskFactor,
  getColor,
  lightenColor,
} from "../../survey/utils/score-utils";
import { CustomTooltip } from "../../shared/views/CustomTooltip";
import { tScoreType } from "../../coding/utils/score-utils";

export const HlaScoreView = ({ score }: { score?: Score }) => {
  return (
    <Stack alignItems="center" gap={4} width="full">
      <Stack gap={1} alignItems="center">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="fg.muted"
          letterSpacing="wide"
          textTransform="uppercase"
        >
          High-level score
        </Text>

        <HlaScoreEntryView score={score?.global} />
      </Stack>

      <SimpleGrid columns={4} gap={4}>
        {Object.values(CrashType).map((crashType) => (
          <RiskScoreCard
            key={crashType}
            score={score?.[crashType]}
            icon={icons[crashType]}
            info={crashType}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

const RiskScoreCard = ({
  score,
  icon,
  info,
}: {
  score?: ScoreEntry;
  icon: React.ReactNode;
  info: keyof Score;
}) => {
  const color = getColor(score?.riskFactor);

  return (
    <CustomTooltip content={tScoreType(info)}>
      <Stack
        alignItems="center"
        justifyContent="center"
        minWidth="60px"
        padding={1}
        borderRadius="lg"
        backgroundColor={color}
        boxShadow="sm"
        borderWidth="1px"
        borderColor="blackAlpha.200"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxSize="38px"
          borderRadius="full"
          backgroundColor="whiteAlpha.700"
          color="black"
        >
          {icon}
        </Box>

        <Box
          minWidth="60px"
          paddingX={2}
          paddingY={0.5}
          borderRadius="md"
          backgroundColor="whiteAlpha.800"
          color="black"
          fontSize="xs"
          fontWeight={"semibold"}
          lineHeight="short"
          textAlign="center"
          textTransform="capitalize"
        >
          {score ? formatRiskFactor(score.riskFactor) : "–"}
        </Box>
      </Stack>
    </CustomTooltip>
  );
};

export const HlaScoreEntryView = ({ score }: { score?: ScoreEntry }) => {
  const color = getColor(score?.riskFactor);
  const lightColor = lightenColor(color, 0.72);

  return (
    <Box
      backgroundColor={color}
      borderRadius="lg"
      padding={1}
      borderWidth="1px"
      borderColor="blackAlpha.200"
      boxShadow="sm"
    >
      <Box
        minWidth="70px"
        paddingX={2}
        paddingY={1}
        borderRadius="md"
        backgroundColor={lightColor}
        color="black"
        fontWeight="bold"
        fontSize="sm"
        lineHeight="short"
        textAlign="center"
      >
        {score ? formatRiskFactor(score.riskFactor) : "–"}
      </Box>
    </Box>
  );
};

const wrapIcon = (icon?: IconType) => (
  <HStack position="relative" boxSize="30px" justifyContent="center" margin={1}>
    {icon ? (
      <>
        <Icon
          fontSize="sm"
          position="absolute"
          top={0}
          left={0}
          as={BicycleIcon}
        />

        <Icon
          fontSize="lg"
          position="absolute"
          right={0}
          bottom={0}
          as={icon}
        />
      </>
    ) : (
      <Icon fontSize="xl" as={BicycleIcon} />
    )}
  </HStack>
);

const icons = {
  [CrashType.vehicleToBicycle]: wrapIcon(CarIcon),
  [CrashType.bicycleToBicycle]: wrapIcon(BicycleIcon),
  [CrashType.bicycleToPedestrian]: wrapIcon(WalkIcon),
  [CrashType.singleBicycle]: wrapIcon(),
};
